import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Psychology, ThumbUp, CheckCircle, Cancel, Delete } from '@mui/icons-material';
import { enhancedApi as jotcoachApi } from '@store/generated/jotcoach';
import type { SessionListItemResponse } from '@store/generated/jotcoach';

type SessionListItemWithActions = SessionListItemResponse & { actions?: string };
import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { DataTable } from '../components/data-table';
import {
  JotCoachSessionFilters,
  type SessionFilters,
} from '../components/jotcoach-session-filters';
import { JotCoachSessionDetailDialog } from '../components/jotcoach-session-detail-dialog';
import { formatDateTime, formatRelativeTime } from '../utils/formatting';

const { useGetV2AdminJotcoachSessionsQuery, useDeleteV2AdminJotcoachSessionsBySessionIdMutation } =
  jotcoachApi;

export default function JotCoachSessionsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);

  // Parse filters from URL
  const page = parseInt(searchParams.get('page') || '1', 10);
  const perPage = parseInt(searchParams.get('per_page') || '20', 10);
  const filters: SessionFilters = {
    organizationId: searchParams.get('organization_id') || undefined,
    userId: searchParams.get('user_id') || undefined,
    noteId: searchParams.get('note_id') || undefined,
    startDate: searchParams.get('start_date') || undefined,
    endDate: searchParams.get('end_date') || undefined,
    dateField: (searchParams.get('date_field') as SessionFilters['dateField']) || 'created_at',
    smsStatus: (searchParams.get('sms_status') as SessionFilters['smsStatus']) || 'all',
    feedbackStatus:
      (searchParams.get('feedback_status') as SessionFilters['feedbackStatus']) || 'all',
  };

  // Fetch sessions data - only include parameters if they're set
  const {
    data: sessionsData,
    isLoading,
    error,
    refetch,
  } = useGetV2AdminJotcoachSessionsQuery({
    page,
    perPage,
    ...(filters.organizationId && { organizationId: filters.organizationId }),
    ...(filters.userId && { userId: filters.userId }),
    ...(filters.noteId && { noteId: filters.noteId }),
    ...(filters.startDate && { startDate: filters.startDate }),
    ...(filters.endDate && { endDate: filters.endDate }),
    ...(filters.dateField && { dateField: filters.dateField }),
    ...(filters.smsStatus && filters.smsStatus !== 'all' && { smsStatus: filters.smsStatus }),
    ...(filters.feedbackStatus &&
      filters.feedbackStatus !== 'all' && { feedbackStatus: filters.feedbackStatus }),
  });

  const [deleteSession, { isLoading: isDeleting }] =
    useDeleteV2AdminJotcoachSessionsBySessionIdMutation();

  const handleFiltersChange = useCallback(
    (newFilters: SessionFilters) => {
      const params = new URLSearchParams();
      params.set('page', '1'); // Reset to first page on filter change

      if (newFilters.organizationId) params.set('organization_id', newFilters.organizationId);
      if (newFilters.userId) params.set('user_id', newFilters.userId);
      if (newFilters.noteId) params.set('note_id', newFilters.noteId);
      if (newFilters.startDate) params.set('start_date', newFilters.startDate);
      if (newFilters.endDate) params.set('end_date', newFilters.endDate);
      if (newFilters.dateField) params.set('date_field', newFilters.dateField);
      if (newFilters.smsStatus && newFilters.smsStatus !== 'all')
        params.set('sms_status', newFilters.smsStatus);
      if (newFilters.feedbackStatus && newFilters.feedbackStatus !== 'all')
        params.set('feedback_status', newFilters.feedbackStatus);

      setSearchParams(params);
    },
    [setSearchParams],
  );

  const handleClearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      const params = new URLSearchParams(searchParams);
      params.set('page', newPage.toString());
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handlePerPageChange = useCallback(
    (newPerPage: number) => {
      const params = new URLSearchParams(searchParams);
      params.set('per_page', newPerPage.toString());
      params.set('page', '1');
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleRowClick = useCallback((session: SessionListItemResponse) => {
    setSelectedSessionId(session.jotcoach_session_id);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedSessionId(null);
  }, []);

  const handleDeleteClick = useCallback((sessionId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSessionToDelete(sessionId);
    setDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!sessionToDelete) return;

    try {
      await deleteSession({ sessionId: sessionToDelete }).unwrap();
      setDeleteConfirmOpen(false);
      setSessionToDelete(null);
      void refetch();
    } catch (err) {
      console.error('Failed to delete session:', err);
    }
  }, [sessionToDelete, deleteSession, refetch]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    setSessionToDelete(null);
  }, []);

  const columns = useMemo(
    () => [
      {
        key: 'jotcoach_session_id' as keyof SessionListItemResponse,
        label: 'Session ID',
        render: (value: unknown) => (
          <Typography variant="body2" fontFamily="monospace" fontSize="0.85rem">
            {String(value).substring(0, 12)}...
          </Typography>
        ),
      },
      {
        key: 'organization_name' as keyof SessionListItemResponse,
        label: 'Organization',
        render: (_value: unknown, row: SessionListItemResponse) => (
          <Typography variant="body2">
            {row.organization_name ||
              (row.organization_id ? `Org ${row.organization_id.substring(0, 8)}...` : 'N/A')}
          </Typography>
        ),
      },
      {
        key: 'user_name' as keyof SessionListItemResponse,
        label: 'Provider',
        render: (_value: unknown, row: SessionListItemResponse) => (
          <Box>
            <Typography variant="body2">
              {row.user_name || `User ${row.user_id.substring(0, 8)}...`}
            </Typography>
            {row.user_email && (
              <Typography variant="caption" color="text.secondary">
                {row.user_email}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        key: 'created_at' as keyof SessionListItemResponse,
        label: 'Created',
        render: (value: unknown) => (
          <Box>
            <Typography variant="body2">{formatRelativeTime(value as string)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDateTime(value as string)}
            </Typography>
          </Box>
        ),
      },
      {
        key: 'has_sms' as keyof SessionListItemResponse,
        label: 'SMS',
        render: (value: unknown) =>
          value ? (
            <Chip
              icon={<CheckCircle />}
              label="Sent"
              size="small"
              color="success"
              variant="outlined"
            />
          ) : (
            <Chip
              icon={<Cancel />}
              label="Not Sent"
              size="small"
              color="default"
              variant="outlined"
            />
          ),
      },
      {
        key: 'feedback_count' as keyof SessionListItemResponse,
        label: 'Feedback',
        render: (value: unknown) => {
          const count = typeof value === 'number' ? value : 0;
          if (count === 0) {
            return (
              <Typography variant="body2" color="text.secondary">
                None
              </Typography>
            );
          }
          const color = count >= 3 ? 'success' : count >= 1 ? 'warning' : 'default';
          return (
            <Chip
              icon={<ThumbUp />}
              label={`${count} item${count > 1 ? 's' : ''}`}
              size="small"
              color={color}
              variant="outlined"
            />
          );
        },
      },
      {
        key: 'actions' as keyof SessionListItemWithActions,
        label: 'Actions',
        render: (_: unknown, row: SessionListItemWithActions) => (
          <Button
            size="small"
            color="error"
            startIcon={<Delete />}
            onClick={e => handleDeleteClick(row.jotcoach_session_id, e)}
          >
            Delete
          </Button>
        ),
      },
    ],
    [handleDeleteClick],
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2/jotcoach" />
        </div>

        {/* Page Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box display="flex" alignItems="center" gap={2}>
            <Psychology fontSize="large" color="primary" />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                JotCoach Sessions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Browse and manage all coaching sessions
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Filters */}
        <JotCoachSessionFilters
          filters={filters}
          onChange={handleFiltersChange}
          onClear={handleClearFilters}
        />

        {/* Sessions Table */}
        {error ? (
          <Box textAlign="center" py={8}>
            <Typography color="error" variant="h6" mb={2}>
              Failed to load sessions
            </Typography>
            <Button onClick={() => void refetch()} variant="outlined">
              Retry
            </Button>
          </Box>
        ) : (
          <>
            <DataTable<SessionListItemWithActions>
              title={`Sessions (${sessionsData?.items.length || 0} of ${sessionsData?.total || 0})`}
              columns={columns}
              data={(sessionsData?.items || []).map(item => ({ ...item, actions: 'actions' }))}
              total={sessionsData?.total}
              loading={isLoading}
              onRowClick={handleRowClick}
              emptyMessage={
                Object.values(filters).some(v => v !== undefined && v !== 'all')
                  ? 'No sessions match the current filters'
                  : 'No sessions found'
              }
            />

            {/* Pagination Controls */}
            {sessionsData && sessionsData.total && sessionsData.total > perPage && (
              <Box
                className="bg-white rounded-lg shadow px-6 py-4 mt-4"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="body2" color="text.secondary">
                  Showing {Math.min((page - 1) * perPage + 1, sessionsData.total)} to{' '}
                  {Math.min(page * perPage, sessionsData.total)} of {sessionsData.total} results
                </Typography>

                <Box display="flex" alignItems="center" gap={2}>
                  <FormControl size="small">
                    <InputLabel>Per Page</InputLabel>
                    <Select
                      value={perPage}
                      label="Per Page"
                      onChange={e => handlePerPageChange(Number(e.target.value))}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </FormControl>

                  <Box display="flex" alignItems="center" gap={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={page <= 1}
                      onClick={() => handlePageChange(page - 1)}
                    >
                      Previous
                    </Button>

                    <Typography variant="body2" color="text.secondary">
                      Page {page} of{' '}
                      {sessionsData.total_pages || Math.ceil(sessionsData.total / perPage)}
                    </Typography>

                    <Button
                      variant="outlined"
                      size="small"
                      disabled={
                        page >=
                        (sessionsData.total_pages || Math.ceil(sessionsData.total / perPage))
                      }
                      onClick={() => handlePageChange(page + 1)}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </>
        )}

        {/* Session Detail Dialog */}
        <JotCoachSessionDetailDialog
          open={selectedSessionId !== null}
          onClose={handleCloseDetail}
          sessionId={selectedSessionId}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onClose={handleCancelDelete}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this JotCoach session? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </main>
    </div>
  );
}
